import { Fragment } from 'react'
import type { ResolvedState, Track } from '@/api/types'
import { describeState } from '@/domain/stateDescriptions'
import { progressOf } from '@/domain/track'
import { Icon } from './Icons'

/**
 * El color nunca es la única señal: el paso actual lleva anillo además de
 * color, y la etapa va enunciada en el `aria-label`.
 */

export function PipelineSm({ state, track }: { state: ResolvedState; track: Track }) {
  const p = progressOf(state.step, track)
  const label = state.unknown
    ? 'Estado no disponible'
    : `Etapa ${(state.step ?? 0) + 1} de ${p.totalSteps} (${p.percent}%)`

  return (
    <span className={`pl-sm${state.unknown ? ' unknown' : ''}`} role="img" aria-label={label}>
      {track.steps.map((s, i) => {
        const reached = !state.unknown && state.step !== null && i <= state.step
        const done = !state.unknown && state.step !== null && i < state.step
        const cur = !state.unknown && state.step !== null && i === state.step
        return (
          <Fragment key={s.code}>
            {i > 0 && <span className={`c${reached ? ' done' : ''}`} />}
            <span className={`n${done ? ' done' : cur ? ' cur' : ''}`} />
          </Fragment>
        )
      })}
    </span>
  )
}

function statusOf(state: ResolvedState, step: number): 'done' | 'cur' | 'wait' {
  if (state.step === null) return 'wait'
  if (step < state.step) return 'done'
  if (step === state.step) return 'cur'
  return 'wait'
}

export function PipelineLg({ state, track }: { state: ResolvedState; track: Track }) {
  if (state.unknown) {
    return (
      <div className="unknown-box">
        <span style={{ color: 'var(--ink-3)', flex: 'none' }}>
          <Icon name="alert" size={18} />
        </span>
        <div>
          <p className="t">Estado no disponible</p>
          <p className="d">
            Esta tarea está en un estado que ya no forma parte del flujo. Se muestra sin avance
            hasta que se actualice.
          </p>
        </div>
      </div>
    )
  }

  const current = track.steps.find((s) => s.step === state.step)
  const currentCode = state.isBranch ? state.branchCode : (current?.code ?? null)

  return (
    <>
      {/* Vertical — hasta 768 px. Es el bloque "Seguimiento" del PNG. */}
      <ol className="tl">
        {track.steps.map((s) => {
          const st = statusOf(state, s.step)
          return (
            <li key={s.code} className={`tl-item ${st}`} aria-current={st === 'cur' ? 'step' : undefined}>
              <span className="tl-node">
                <Icon name={s.icon} size={st === 'wait' ? 16 : 17} />
              </span>
              <div>
                <p className="tl-ttl">{s.label}</p>
                <p className="tl-desc">{describeState(s.code)}</p>
                {st === 'cur' && state.isBranch && (
                  <div className="tl-branch">
                    <span className="pill pill--blue">
                      <span className="dot" />
                      {state.branchLabel}
                    </span>
                    <p className="tl-desc" style={{ marginTop: 6 }}>
                      {describeState(state.branchCode)}
                    </p>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      {/* Horizontal — desde 768 px. Con 8 pasos no cabe una descripción bajo
          cada círculo, así que se muestran los nombres y la descripción del
          estado actual queda en el panel de abajo. */}
      <div className="tlh">
        <div className="tlh-row">
          {track.steps.map((s) => {
            const st = statusOf(state, s.step)
            return (
              <div
                key={s.code}
                className={`tlh-step ${st}`}
                aria-current={st === 'cur' ? 'step' : undefined}
              >
                <span className="tlh-node">
                  <Icon name={s.icon} size={st === 'wait' ? 16 : 17} />
                </span>
                <span className="tlh-lbl">{s.label}</span>
              </div>
            )
          })}
        </div>
        <div className="tlh-now">
          <span style={{ color: 'var(--live)', flex: 'none', marginTop: 1 }}>
            <Icon name={current?.icon ?? 'alert'} size={16} />
          </span>
          <div>
            <p className="k">Ahora: {state.isBranch ? state.branchLabel : current?.label}</p>
            <p className="d">{describeState(currentCode)}</p>
          </div>
        </div>
      </div>
    </>
  )
}
